<template>
  <v-container fluid class="admin-intake">
    <NcrmHomePageHeader
      icon="mdi-account-arrow-right-outline"
      :title="$t('admin.workflows.selfRegistrationIntake.title')"
      :subtitle="$t('admin.workflows.selfRegistrationIntake.subtitle')"
    >
      <v-btn color="secondary" variant="tonal" @click="$emit('back')">
        <v-icon start>mdi-arrow-left</v-icon>
        {{ $t("admin.workflows.actions.backToList") }}
      </v-btn>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage && !selectedIntake" type="error" class="mt-4">
      {{ errorMessage }}
    </v-alert>
    <v-alert v-if="successMessage && !selectedIntake" type="success" class="mt-4">
      {{ successMessage }}
    </v-alert>

    <v-alert
      v-if="configurationMissing"
      type="warning"
      class="mt-4"
      :title="$t('admin.workflows.selfRegistrationIntake.alerts.configurationRequiredTitle')"
    >
      {{ $t("admin.workflows.selfRegistrationIntake.alerts.configurationRequiredBody") }}
    </v-alert>
    <v-alert v-else-if="dispatchFlagDisabled" type="info" class="mt-4">
      {{ $t("admin.workflows.selfRegistrationIntake.alerts.flagDisabled") }}
    </v-alert>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-intake__panel mt-4">
      <div class="admin-intake__panel-header">
        <div>
          <h2>{{ $t("admin.workflows.selfRegistrationIntake.settings.title") }}</h2>
          <p>{{ $t("admin.workflows.selfRegistrationIntake.settings.subtitle") }}</p>
        </div>
        <v-chip :color="dispatchActive ? 'success' : 'warning'">
          {{
            dispatchActive
              ? $t("admin.workflows.selfRegistrationIntake.settings.active")
              : $t("admin.workflows.selfRegistrationIntake.settings.inactive")
          }}
        </v-chip>
      </div>

      <v-skeleton-loader v-if="loading && !overview" type="article, actions" />
      <v-form v-else class="admin-intake__form" @submit.prevent="saveSettings">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              :model-value="form.targetCompanyId"
              :items="companyItems"
              item-title="label"
              item-value="value"
              :label="$t('admin.workflows.selfRegistrationIntake.fields.targetCompany')"
              hide-details="auto"
              @update:model-value="handleTargetCompanyChange"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.externalApiEndpointId"
              :items="endpointItems"
              item-title="label"
              item-value="value"
              :label="$t('admin.workflows.selfRegistrationIntake.fields.endpoint')"
              :disabled="!form.targetCompanyId"
              :hint="$t('admin.workflows.selfRegistrationIntake.fields.endpointHint')"
              persistent-hint
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.enrichmentWorkflowInstanceId"
              :items="workflowItems"
              item-title="label"
              item-value="value"
              :label="$t('admin.workflows.selfRegistrationIntake.fields.enrichmentWorkflow')"
              :disabled="!form.targetCompanyId"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="form.apiKey"
              type="password"
              autocomplete="new-password"
              :label="$t('admin.workflows.selfRegistrationIntake.fields.apiKey')"
              :placeholder="
                hasStoredApiKey
                  ? $t('admin.workflows.selfRegistrationIntake.fields.apiKeyStored')
                  : ''
              "
              :hint="$t('admin.workflows.selfRegistrationIntake.fields.apiKeyHint')"
              persistent-hint
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="form.enabled"
              color="primary"
              density="comfortable"
              hide-details
              :label="$t('admin.workflows.selfRegistrationIntake.fields.enabled')"
            />
          </v-col>
        </v-row>

        <div class="admin-intake__actions">
          <v-btn color="primary" type="submit" :loading="saving" :disabled="!canSave">
            <v-icon start>mdi-content-save-outline</v-icon>
            {{ $t("admin.workflows.actions.save") }}
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <v-card variant="flat" class="admin-intake__panel mt-4">
      <div class="admin-intake__panel-header">
        <div>
          <h2>{{ $t("admin.workflows.selfRegistrationIntake.board.title") }}</h2>
          <p>{{ $t("admin.workflows.selfRegistrationIntake.board.subtitle") }}</p>
        </div>
      </div>

      <v-row dense class="admin-intake__filters">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="filters.search"
            :label="$t('admin.workflows.selfRegistrationIntake.filters.search')"
            prepend-inner-icon="mdi-magnify"
            hide-details
            clearable
            @update:model-value="applyFilters"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="filters.status"
            :items="statusFilterItems"
            item-title="label"
            item-value="value"
            :label="$t('admin.workflows.selfRegistrationIntake.filters.status')"
            hide-details
            @update:model-value="applyFilters"
          />
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="intakeRows"
        :loading="loading"
        item-key="id"
        density="comfortable"
        hide-default-footer
        :items-per-page="-1"
        :row-props="intakeRowProps"
        @click:row="handleIntakeClick"
      >
        <template #item.contact="{ item }">
          <div class="min-w-0">
            <div class="admin-intake__primary">{{ item.contact }}</div>
            <div class="admin-intake__secondary">{{ item.email }}</div>
          </div>
        </template>

        <template #item.product="{ item }">
          {{ productLabel(item.product) }}
        </template>

        <template #item.status="{ item }">
          <v-chip :color="statusColor(item.status)">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template #item.action>
          <v-icon size="18" class="admin-intake__row-chevron">mdi-chevron-right</v-icon>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("admin.workflows.selfRegistrationIntake.board.empty") }}
          </v-alert>
        </template>
      </v-data-table>

      <div v-if="pageCount > 1" class="admin-intake__pagination">
        <v-pagination
          :model-value="page"
          :length="pageCount"
          density="comfortable"
          total-visible="5"
          @update:model-value="changePage"
        />
      </div>
    </v-card>

    <AdminSelfRegistrationIntakeDetail
      :intake="selectedIntake"
      :retrying="retryingId === selectedIntake?.id"
      :error-message="errorMessage"
      :success-message="successMessage"
      :format-date="formatDate"
      :status-label="statusLabel"
      :product-label="productLabel"
      @close="closeIntake"
      @retry="retryIntake"
    />
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import AdminSelfRegistrationIntakeDetail from "./AdminSelfRegistrationIntakeDetail.vue";
import adminSelfRegistrationIntakeLogic from "./adminSelfRegistrationIntakeLogic.mjs";

export default {
  name: "AdminSelfRegistrationIntakeWorkflow",
  components: {
    AdminSelfRegistrationIntakeDetail,
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  mixins: [adminSelfRegistrationIntakeLogic],
  props: {
    definition: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["back"],
  methods: {
    intakeRowProps() {
      return {
        class: "admin-intake__row",
        style: "cursor: pointer;",
        tabindex: 0,
      };
    },
    handleIntakeClick(_, row) {
      const intake = row?.item?.raw || null;
      if (!intake) return;
      this.openIntake(intake);
    },
  },
};
</script>

<style scoped>
.admin-intake {
  background: var(--app-page-background);
  min-height: 100%;
}

.admin-intake__panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  padding: 20px;
}

.admin-intake__panel-header {
  /* The app shell centers text globally; panel copy reads better left-aligned. */
  text-align: start;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.admin-intake__panel-header h2 {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 1.05rem;
  font-weight: 700;
}

.admin-intake__panel-header p {
  margin: 4px 0 0;
  color: var(--app-text-default);
  opacity: 0.68;
  line-height: 1.5;
}

.admin-intake__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-intake__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.admin-intake__filters {
  margin-bottom: 12px;
}

.admin-intake__primary {
  color: var(--app-text-strong);
  font-weight: 600;
}

.admin-intake__secondary {
  color: var(--app-text-default);
  font-size: 0.82rem;
  opacity: 0.62;
}

.admin-intake__row-chevron {
  opacity: 0.48;
}

.admin-intake__pagination {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

</style>
